import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import UserAvatar from "../components/UserAvatar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Heart, MessageCircle, UserPlus, Bot, Bell, CheckCheck, Loader2, ArrowLeft, Home, Search, Plus, User } from "lucide-react";
import api from "../lib/api";

function formatRelativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

const NOTIF_ICONS = {
  like: { icon: Heart, color: "text-red-500 bg-red-500/10" },
  comment: { icon: MessageCircle, color: "text-blue-500 bg-blue-500/10" },
  follow: { icon: UserPlus, color: "text-purple-500 bg-purple-500/10" },
  ai_post: { icon: Bot, color: "text-violet-500 bg-violet-500/10" },
  mention: { icon: Bell, color: "text-cyan-400 bg-cyan-500/10" },
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => {});
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications?page=1&limit=50");
      setNotifications(r.data.notifications);
    } catch { toast.error("Failed to load notifications"); }
    finally { setLoading(false); }
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read");
    } catch { toast.error("Failed"); }
  };

  const handleClick = (n) => {
    // Deep-link to the referenced post; FeedPage picks up ?post= and
    // can scroll/highlight the relevant entry.
    if (n.post_id) navigate(`/feed?post=${n.post_id}`);
    else if (n.actor_username) navigate(`/profile/${n.actor_username}`);
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="notifications-page">
      {/* Top bar */}
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="font-outfit text-base font-semibold text-foreground">Notifications</h1>
        </div>
        {notifications.some((n) => !n.is_read) && (
          <button onClick={markAllRead} className="flex items-center gap-1 text-xs text-cyan-400 font-medium bg-transparent border-0 cursor-pointer hover:text-cyan-300" data-testid="mark-all-read-btn">
            <CheckCheck className="w-3.5 h-3.5" /> Mark all read
          </button>
        )}
      </div>

      <div className="max-w-2xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16 animate-fade-in-up" data-testid="notif-empty">
            <Bell className="w-10 h-10 text-muted-foreground/60 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-1">No notifications yet</p>
            <p className="text-xs text-muted-foreground/70">Start interacting to see activity here!</p>
          </div>
        ) : (
          <div data-testid="notifications-list">
            {notifications.map((n) => {
              const config = NOTIF_ICONS[n.type] || NOTIF_ICONS.mention;
              const Icon = config.icon;
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full flex items-start gap-3 px-4 py-3.5 text-left border-0 cursor-pointer transition-colors ${
                    n.is_read
                      ? n.type === "like" ? "bg-pink-500/10 hover:bg-pink-500/10"
                        : n.type === "comment" ? "bg-blue-500/10 hover:bg-blue-500/10"
                        : n.type === "follow" ? "bg-green-500/10 hover:bg-green-500/10"
                        : n.type === "ai_post" ? "bg-purple-500/10 hover:bg-purple-500/10"
                        : "bg-card hover:bg-white/5"
                      : "bg-cyan-500/10 hover:bg-cyan-500/10"
                  } border-b border-border/60`}
                  data-testid={`notif-item-${n.id}`}
                >
                  {/* Actor avatar or icon */}
                  <div className="flex-shrink-0 relative">
                    {n.actor_avatar_url || n.actor_display_name ? (
                      <UserAvatar name={n.actor_display_name || ""} username={n.actor_username || ""} avatarUrl={n.actor_avatar_url} size="sm" />
                    ) : (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                    )}
                    <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ${config.color} ring-2 ring-background`}>
                      <Icon className="w-2.5 h-2.5" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">{n.message}</p>
                    <p className="text-[11px] text-muted-foreground/70 mt-0.5">{formatRelativeTime(n.created_at)}</p>
                  </div>
                  {!n.is_read && <div className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0 mt-2" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Mobile Bottom Tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border/60 h-14 flex items-center justify-around px-2">
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Home className="w-5 h-5" /><span className="text-[10px]">Home</span></Link>
        <Link to="/explore" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Search className="w-5 h-5" /><span className="text-[10px]">Explore</span></Link>
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Plus className="w-5 h-5" /></Link>
        <Link to="/notifications" className="flex flex-col items-center gap-0.5 no-underline text-cyan-400"><Bell className="w-5 h-5" /><span className="text-[10px]">Notifs</span></Link>
        <Link to={profile ? `/profile/${profile.username}` : "/feed"} className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><User className="w-5 h-5" /><span className="text-[10px]">Profile</span></Link>
      </div>
    </div>
  );
}
